"use client";

import { c as _c } from "react/compiler-runtime";
import { CheckCircleIcon, ClockIcon, CircleDashedIcon, XCircleIcon, type Icon } from "@phosphor-icons/react";
import { useState } from "react";
import CTAButton from "@/src/components/cta-button";
import { TASK_STEP_PHASE_PRESENTATION } from "@/src/components/tasks/task-step-phase";
import { ValerieCard, type ValerieCardHeaderVariant } from "@/src/components/valerie-card";
import { toast } from "@/src/hooks/use-toast";
import { useTaskStepVerifications, useUpsertTaskStepVerificationMutation } from "@/src/lib/data-fetchers/tasks-fetchers";
import { cn } from "@/src/lib/shadcn-utils";
import { resolveTaskStepPhase, TaskStepCardInactiveStatus, TaskStepPhase, type PinnableTaskStepPhase, type TaskStepCardState } from "@/src/models/task-step-card";
import { VerificationResult } from "@/src/models/task-verification";
type TaskStepCardPresentation = {
  collapsed?: boolean;
  description: string;
  icon: Icon;
  label: string;
  variant: ValerieCardHeaderVariant;
  /** Strikes the card title, marking a step a human reviewed and rejected. */
  strikeHeader?: boolean;
};
const PHASE_DESCRIPTION: Record<TaskStepPhase, string> = {
  [TaskStepPhase.QA]: "Automation completed this step. Spot-check it below, then mark it correct or incorrect.",
  [TaskStepPhase.ACTION_NEEDED]: "Automation can't finish this one. Complete it in Athena, then continue.",
  [TaskStepPhase.FAILED]: "Automation should have completed this step and didn't. Fix it in Athena, then continue."
};
export function TaskStepCard(t0) {
  const $ = _c(28);
  const {
    header,
    status,
    phase: pinnedPhase,
    detail,
    taskId,
    stepKey,
    workflowStepKeys,
    onVerificationComplete,
    action,
    children
  } = t0;
  const resolvedPhase = resolveTaskStepPhase(status, pinnedPhase);
  const basePresentation = getTaskStepCardPresentation(status, resolvedPhase, detail);
  const {
    verdictByStepKey
  } = useTaskStepVerifications(taskId);
  const footerAction = resolvedPhase === TaskStepPhase.ACTION_NEEDED || resolvedPhase === TaskStepPhase.FAILED ? action : null;
  const recordsVerdict = resolvedPhase === TaskStepPhase.QA || resolvedPhase === TaskStepPhase.ACTION_NEEDED || resolvedPhase === TaskStepPhase.FAILED && !footerAction;
  const verdict = recordsVerdict ? verdictByStepKey.get(stepKey)?.verdict : undefined;
  const presentation = applyVerdictToPresentation(basePresentation, resolvedPhase, verdict);
  const PhaseIcon = presentation.icon;
  let t1;
  if ($[0] !== footerAction || $[1] !== onVerificationComplete || $[2] !== recordsVerdict || $[3] !== resolvedPhase || $[4] !== stepKey || $[5] !== taskId || $[6] !== verdict || $[7] !== workflowStepKeys) {
    t1 = footerAction || recordsVerdict ? <div className="flex flex-col gap-3 border-t pt-3">{footerAction}{recordsVerdict && resolvedPhase ? <TaskStepVerificationControl phase={resolvedPhase} taskId={taskId} stepKey={stepKey} workflowStepKeys={workflowStepKeys} verdict={verdict} onComplete={footerAction ? undefined : onVerificationComplete} /> : null}</div> : null;
    $[0] = footerAction;
    $[1] = onVerificationComplete;
    $[2] = recordsVerdict;
    $[3] = resolvedPhase;
    $[4] = stepKey;
    $[5] = taskId;
    $[6] = verdict;
    $[7] = workflowStepKeys;
    $[8] = t1;
  } else {
    t1 = $[8];
  }
  const footer = t1;
  let t2;
  if ($[9] !== header || $[10] !== presentation.strikeHeader) {
    t2 = presentation.strikeHeader ? <span className="line-through">{header}</span> : header;
    $[9] = header;
    $[10] = presentation.strikeHeader;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const t3 = presentation.collapsed ? "bg-muted/20 text-muted-foreground opacity-40 grayscale" : undefined;
  let t4;
  if ($[12] !== PhaseIcon) {
    t4 = <PhaseIcon weight="fill" className="size-4 shrink-0" />;
    $[12] = PhaseIcon;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== presentation.label || $[15] !== t4) {
    t5 = <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-current/30 bg-background/15 px-2 py-0.5 text-xs font-medium text-current">{t4}{presentation.label}</span>;
    $[14] = presentation.label;
    $[15] = t4;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== children || $[18] !== footer || $[19] !== presentation.collapsed || $[20] !== presentation.description) {
    t6 = presentation.collapsed ? <details><summary className="text-sm text-muted-foreground">{presentation.description}</summary><div className="mt-3 space-y-3">{children}</div></details> : <><p className="text-sm text-muted-foreground">{presentation.description}</p>{children}{footer}</>;
    $[17] = children;
    $[18] = footer;
    $[19] = presentation.collapsed;
    $[20] = presentation.description;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== presentation.variant || $[23] !== t2 || $[24] !== t3 || $[25] !== t5 || $[26] !== t6) {
    t7 = <ValerieCard title={t2} variant={presentation.variant} className={t3} actions={t5} contentClassName="space-y-3">{t6}</ValerieCard>;
    $[22] = presentation.variant;
    $[23] = t2;
    $[24] = t3;
    $[25] = t5;
    $[26] = t6;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  return t7;
}

/**
 * Folds ops' recorded verdict into how the card presents.
 *
 * A step in a verdict phase deepens to `verified` green only once ops confirms it. That is the
 * only route to the dark green: a `QA` card is already green while it waits, but automation
 * finishing a step is not the same as anyone having signed off on it.
 *
 * A rejected step goes red, the same colour as a sync failure, because both mean "this is wrong
 * and needs fixing". What separates them is that a rejection was a human decision, so the title is
 * struck through and the ✗ stays lit in the footer — a failed sync has neither.
 */
function applyVerdictToPresentation(presentation: TaskStepCardPresentation, phase: TaskStepPhase | null, verdict: VerificationResult | undefined): TaskStepCardPresentation {
  if (verdict === VerificationResult.CORRECT) {
    // Only `QA` is ops checking automation's work; on the other two they did or fixed it
    // themselves, so "Verified" would overclaim.
    const resolution = phase === TaskStepPhase.ACTION_NEEDED ? {
      label: "Updated",
      description: "Completed by ops."
    } : phase === TaskStepPhase.FAILED ? {
      label: "Resolved",
      description: "Fixed by ops."
    } : {
      label: "Verified",
      description: "Verified by ops as correct."
    };
    return {
      ...presentation,
      ...resolution,
      icon: CheckCircleIcon,
      variant: "verified"
    };
  }
  if (verdict === VerificationResult.INCORRECT) {
    return {
      ...presentation,
      description: "Marked incorrect by ops. Correct it, then mark this step verified.",
      icon: XCircleIcon,
      label: "Incorrect",
      variant: "failed",
      strikeHeader: true
    };
  }
  return presentation;
}
function getTaskStepCardPresentation(status: TaskStepCardState, phase: TaskStepPhase | null, detail?: string): TaskStepCardPresentation {
  if (phase) {
    const {
      label,
      icon,
      variant
    } = TASK_STEP_PHASE_PRESENTATION[phase];
    return {
      description: detail ?? PHASE_DESCRIPTION[phase],
      icon,
      label,
      variant
    };
  }
  switch (status) {
    case TaskStepCardInactiveStatus.NOT_NEEDED:
      return {
        collapsed: true,
        description: "This step is not needed.",
        icon: CircleDashedIcon,
        label: "Not needed",
        variant: "outline"
      };
    /**
     * `WAITING`, `NOT_RUN`, and the exhaustiveness backstop: a phase covers every actionable
     * status, so anything reaching here is a neutral timeline state.
     *
     * Waiting and not-run are deliberately one presentation. The distinction was never real to
     * ops — both mean "this hasn't happened yet" — and it wasn't even reliable underneath, since
     * `taskStepStatus` returns `waiting` as its catch-all for a step whose row doesn't exist.
     * Two labels for one situation just invited the question of which was which.
     */
    default:
      return {
        description: detail ?? "Waiting for this step to complete.",
        icon: ClockIcon,
        label: "Waiting",
        variant: "outline"
      };
  }
}

/**
 * What ops records in the card footer, which differs by phase because the question does.
 *
 * `QA` asks "did automation get this right?" — a Correct / Incorrect pair. The other two have
 * only one answer worth a button: `ACTION_NEEDED` asks "have you done it?" (**Updated**) and
 * `FAILED` asks "have you fixed it?" (**Continue**). All three write the same verdict row, so a
 * resolved step reads the same way to downstream accuracy reporting however it got there.
 */
function TaskStepVerificationControl({
  phase,
  taskId,
  stepKey,
  workflowStepKeys,
  verdict,
  onComplete
}: {
  phase: TaskStepPhase;
  taskId: string;
  stepKey: string;
  workflowStepKeys: string[];
  verdict: VerificationResult | undefined;
  onComplete?: (verdict: VerificationResult) => Promise<void>;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const upsertMutation = useUpsertTaskStepVerificationMutation();
  const handleClick = async (next: VerificationResult) => {
    // A rejection records the verdict and nothing else: releasing the workflow on `INCORRECT` is
    // the one thing ops was saying not to do. So re-clicking the recorded verdict is only worth a
    // round trip when there is a release to (re)attempt, which is `CORRECT` alone.
    const releasesWorkflow = next === VerificationResult.CORRECT && Boolean(onComplete);
    if (verdict === next && !releasesWorkflow) return;
    setIsSubmitting(true);
    try {
      /**
       * Release before recording, because the release is allowed to refuse.
       *
       * On a gated card `onComplete` approves the pending gate, and an approval can re-validate
       * against the EHR and reject — TPR's coverage gate re-reads Athena and throws when the
       * coverage still doesn't line up. Recording first meant that rejection left a step marked
       * Verified: dark green, struck off the outstanding list, and the workflow still parked with
       * its approval pending, so every step behind the gate silently never ran.
       *
       * This way a refused release records nothing, the card stays outstanding, and ops sees why.
       * The reverse risk — released but unrecorded, if the verdict write fails — is recoverable by
       * clicking again, which a green card on a stalled workflow is not.
       */
      if (releasesWorkflow) await onComplete?.(next);
      if (verdict !== next) {
        await upsertMutation.mutateAsync({
          taskId,
          payload: {
            stepKey,
            workflowStepKeys,
            verdict: next
          }
        });
      }
    } catch (error) {
      toast({
        title: releasesWorkflow ? "The workflow couldn't continue, so this step wasn't marked" : "Couldn't save that",
        description: error instanceof Error ? error.message : "Refresh the task and try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  const isBusy = isSubmitting || upsertMutation.isPending;
  if (phase !== TaskStepPhase.QA) {
    return <div className="mx-auto flex w-full max-w-40">
        <VerdictButton label={phase === TaskStepPhase.FAILED ? "Continue" : "Updated"} tone="correct" selected={verdict === VerificationResult.CORRECT} disabled={isBusy} onClick={() => handleClick(VerificationResult.CORRECT)} />
      </div>;
  }
  return (
    // Sized to sit alongside the continuation controls rather than dominate the card: these repeat
    // on all eleven steps, so a page-width CTA on each one made the timeline read as eleven calls
    // to action instead of a checklist.
    <div className="mx-auto flex w-full max-w-sm gap-3">
      <VerdictButton label="Correct" tone="correct" selected={verdict === VerificationResult.CORRECT} disabled={isBusy} onClick={() => handleClick(VerificationResult.CORRECT)} />
      <VerdictButton label="Incorrect" tone="incorrect" selected={verdict === VerificationResult.INCORRECT} disabled={isBusy}
      // Only one control per card may claim cmd+Enter, and it shouldn't be the negative one.
      focusOnCommandEnter={false} onClick={() => handleClick(VerificationResult.INCORRECT)} />
    </div>
  );
}

/**
 * Unselected buttons are outlined and selected ones filled, so which verdict was recorded is
 * readable from the button itself and not only from the card's colour — the footer sits below the
 * fold on a long card.
 *
 * Correct is drawn in `verified`, the colour the card itself turns once it's clicked, so the
 * button previews its own outcome.
 */
const VERDICT_BUTTON_CLASS = {
  correct: {
    on: "border-verified bg-verified text-verified-foreground hover:bg-verified/90",
    off: "border-verified/50 text-verified hover:border-verified hover:bg-verified/10"
  },
  incorrect: {
    on: "border-failed bg-failed text-failed-foreground hover:bg-failed/90",
    off: "border-failed/50 text-failed hover:border-failed hover:bg-failed/10"
  }
} as const;
function VerdictButton(t0) {
  const $ = _c(12);
  const {
    label,
    tone,
    selected,
    disabled,
    focusOnCommandEnter,
    onClick
  } = t0;
  const {
    on,
    off
  } = VERDICT_BUTTON_CLASS[tone];
  let t1;
  if ($[0] !== label) {
    t1 = label.toLowerCase();
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `Mark this step ${t1}`;
  const t3 = selected ? on : off;
  let t4;
  if ($[2] !== t3) {
    t4 = cn("h-8 flex-1 border", t3);
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== disabled || $[5] !== focusOnCommandEnter || $[6] !== label || $[7] !== onClick || $[8] !== selected || $[9] !== t2 || $[10] !== t4) {
    t5 = <CTAButton type="button" variant="outline" small={true} aria-label={t2} aria-pressed={selected} disabled={disabled} focusOnCommandEnter={focusOnCommandEnter} onClick={onClick} className={t4}>{label}</CTAButton>;
    $[4] = disabled;
    $[5] = focusOnCommandEnter;
    $[6] = label;
    $[7] = onClick;
    $[8] = selected;
    $[9] = t2;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}